import React, { createContext, useEffect, useState } from 'react';

import { useUrlParams } from './UrlParamsHook';
import { useSocketIO } from './SocketIOHook';

const UPDATE_EXPIRATION_STATE_INTERVAL_MS = 2000;

const AppLogicContext = createContext();

export const useAppLogic = () => {
  const context = React.useContext(AppLogicContext);
  if (context === undefined) {
    throw new Error('`useAppLogic` hook must be used within a `AppLogicContextProvider` component');
  }
  return context;
};

export const AppLogicContextProvider = ({ children }) => {
  const { roomNumber } = useUrlParams();
  const {
    sendKeonCommand,
    sendPowerblowCommand,
    sendPowerShotCommand: socketIOSendPowerShotCommand,
    isPartnerOnline: socketIOPartnerOnline,
    cooldown: socketIOCooldown,
    sessionMetadata: socketIOSessionMetadata,
  } = useSocketIO();

  const [cooldownIsActive, setCooldownIsActive] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [expirationTime, setExpirationTime] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [partner, setPartner] = useState('');
  const [deviceList, setDeviceList] = useState([]);
  const [isToggleDeviceButtonVisible, setIsToggleDeviceButtonVisible] = useState(false);
  const [isPowerShotConnected, setIsPowerShotConnected] = useState(false);
  const [isStrokerConnected, setIsStrokerConnected] = useState(false);
  const [strokerDeviceName, setStrokerDeviceName] = useState(null);
  const [powerShotDeviceName, setPowerShotDeviceName] = useState(null);
  const [powerBlowDeviceName, setPowerBlowDeviceName] = useState(null);

  // Merge Socket.IO presence into isOnline
  useEffect(() => {
    setIsOnline(socketIOPartnerOnline);
  }, [socketIOPartnerOnline]);

  // Merge Socket.IO cooldown events
  useEffect(() => {
    if (socketIOCooldown === null) {
      return;
    }
    if (socketIOCooldown === 'on') {
      setCooldownIsActive(true);
    } else if (socketIOCooldown === 'off') {
      setCooldownIsActive(false);
    }
  }, [socketIOCooldown]);

  // Merge Socket.IO session metadata
  useEffect(() => {
    if (!socketIOSessionMetadata) {
      return;
    }
    const { sharingEndTime, username, devices } = socketIOSessionMetadata;
    if (sharingEndTime) {
      setExpirationTime(sharingEndTime);
    }
    if (username) {
      setPartner(username);
    }
    if (devices) {
      setDeviceList(Array.isArray(devices) ? devices : JSON.parse(devices));
    }
  }, [socketIOSessionMetadata]);

  useEffect(() => {
    const powerBlow = deviceList?.find((d) => d === 'PowerBlow R1') ?? null;
    const powerShot = deviceList?.find((d) => d.toUpperCase().trim() === 'POWERSHOT') ?? null;
    const keon = deviceList?.find((d) => d !== 'PowerBlow R1' && d.toUpperCase().trim() !== 'POWERSHOT') ?? null;

    setIsToggleDeviceButtonVisible(Boolean(powerBlow));
    setIsPowerShotConnected(Boolean(powerShot));
    setIsStrokerConnected(Boolean(keon));
    setPowerBlowDeviceName(powerBlow);
    setPowerShotDeviceName(powerShot);
    setStrokerDeviceName(keon);
  }, [deviceList]);

  useEffect(() => {
    const updateExpirationState = () => {
      // Check if sharing is expired
      const expired = Boolean(expirationTime && new Date(expirationTime) < new Date());
      setIsExpired(expired);
    };

    updateExpirationState();

    const intervalId = setInterval(updateExpirationState, UPDATE_EXPIRATION_STATE_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [expirationTime]);

  const sendPercent = (percent, position, isRangeMode) => {
    if (!roomNumber) {
      return;
    }

    // send to Socket.IO
    sendKeonCommand(percent, position, isRangeMode);
  };

  const sendPowerblowSettings = (customSettings) => {
    if (!roomNumber) {
      return;
    }

    // send to Socket.IO
    sendPowerblowCommand(customSettings);
  };

  const sendPowerShotMotors = (leftMotorPercent, rightMotorPercent) => {
    if (!roomNumber) {
      return;
    }
    socketIOSendPowerShotCommand(leftMotorPercent, rightMotorPercent);
  };

  return (
    <AppLogicContext.Provider
      value={{
        sendPercent,
        sendPowerblowSettings,
        sendPowerShotMotors,
        isOnline,
        isExpired,
        partner,
        cooldownIsActive,
        isToggleDeviceButtonVisible,
        isPowerShotConnected,
        isStrokerConnected,
        strokerDeviceName,
        powerShotDeviceName,
        powerBlowDeviceName,
      }}
    >
      {children}
    </AppLogicContext.Provider>
  );
};
